// KHIYARSA PATCH: plain-text bullet-list helpers for the list tool ("قائمة")
// and the convert-to-bullet-list action. Text elements stay plain text — a
// "list" is just lines prefixed with a marker character:
//   "☐ " / "☑ "  checklist item (unchecked / checked) — square checkbox
//                 look, toggled by clicking the marker on canvas; checked
//                 items render dimmed with a strikethrough
//   "✓ "         LEGACY checked item (UI-3 v1) — still recognized/toggleable
//                 so existing scenes keep working; new checks write "☑"
//   "• " / "◦ "  plain bullets (typed or via the convert action) — list
//                 alignment applies but they are NOT toggleable

export const BULLET_PRIMARY = "•";
export const BULLET_SECONDARY = "◦";
export const CHECKBOX_EMPTY = "☐";
export const CHECKBOX_CHECKED = "☑";
// legacy checked marker from the first checklist iteration
export const CHECK_MARKER = "✓";

// must stay in sync with the wysiwyg TAB_SIZE
export const LIST_TAB_SIZE = 4;

const BULLET_LINE_RE = /^([ ]*)([•◦☐☑✓]) ?(.*)$/;

// terminator run must be followed by whitespace or end-of-string so decimals
// ("3.14") and dotted tokens survive; covers Latin . ! ?, Arabic ؟ ؛ and the
// danda ।
const SENTENCE_SPLIT_RE = /(?<=[.!?؟؛।])(?![.!?؟؛।])\s+/g;

export type ListMarker =
  | typeof BULLET_PRIMARY
  | typeof BULLET_SECONDARY
  | typeof CHECKBOX_EMPTY
  | typeof CHECKBOX_CHECKED
  | typeof CHECK_MARKER;

/** the line is a toggleable checklist item (not a plain bullet) */
export const isChecklistMarker = (marker: ListMarker): boolean =>
  marker === CHECKBOX_EMPTY ||
  marker === CHECKBOX_CHECKED ||
  marker === CHECK_MARKER;

/** the checklist item is completed */
export const isCheckedMarker = (marker: ListMarker): boolean =>
  marker === CHECKBOX_CHECKED || marker === CHECK_MARKER;

export const matchBulletLine = (
  line: string,
): {
  indent: string;
  marker: ListMarker;
  content: string;
} | null => {
  const match = line.match(BULLET_LINE_RE);
  if (!match) {
    return null;
  }
  return {
    indent: match[1],
    marker: match[2] as ListMarker,
    content: match[3],
  };
};

/**
 * Marker a NEW item created after the given line should get (Enter
 * continuation): checklist items (incl. checked ones) continue as an empty
 * "☐" checkbox — new items always start incomplete; plain bullets continue
 * with their own marker.
 */
export const getContinuationMarker = (match: {
  indent: string;
  marker: ListMarker;
}): typeof BULLET_PRIMARY | typeof BULLET_SECONDARY | typeof CHECKBOX_EMPTY => {
  if (isChecklistMarker(match.marker)) {
    return CHECKBOX_EMPTY;
  }
  return match.marker as typeof BULLET_PRIMARY | typeof BULLET_SECONDARY;
};

/**
 * Toggle the checklist state of the given (0-based, hard) line:
 * "☐ " ⇄ "☑ " (legacy "✓ " unchecks to "☐ "). Plain bullets and non-list
 * lines are returned unchanged — they are not checklist items.
 */
export const toggleChecklistLine = (
  text: string,
  lineIndex: number,
): string => {
  const lines = text.split("\n");
  const line = lines[lineIndex];
  if (line === undefined) {
    return text;
  }
  const match = matchBulletLine(line);
  if (!match || !isChecklistMarker(match.marker)) {
    return text;
  }
  const nextMarker = isCheckedMarker(match.marker)
    ? CHECKBOX_EMPTY
    : CHECKBOX_CHECKED;
  lines[lineIndex] = `${match.indent}${nextMarker} ${match.content}`;
  return lines.join("\n");
};

/** true when at least one hard line is a list line (bullet or completed) */
export const hasListLine = (text: string): boolean =>
  text.split("\n").some((line) => matchBulletLine(line));

export type RenderedListLineState = {
  /** index of the hard (original) line this rendered line belongs to */
  originalLineIndex: number;
  /** the original line is a list item (bullet or completed) */
  isListLine: boolean;
  /** this rendered line carries the item's marker (not a wrapped tail) */
  hasMarker: boolean;
  /** the item is completed ("☑", or legacy "✓") */
  checked: boolean;
  /** direction of the whole item, so wrapped tails align with their item */
  rtl: boolean;
};

const stripWhitespace = (text: string) => text.replace(/\s+/g, "");

/**
 * Map RENDERED (possibly word-wrapped) lines back onto the hard lines of
 * `originalText`, carrying list/checked state to wrapped continuation lines.
 * Wrapping only re-breaks whitespace, so hard lines are matched by consuming
 * non-whitespace characters. `isLineRTL` is injected (isRTL lives in
 * @excalidraw/common) to keep this module dependency-free.
 */
export const getRenderedListLineStates = (
  originalText: string,
  renderedLines: readonly string[],
  isLineRTL: (line: string) => boolean,
): RenderedListLineState[] => {
  const originalLines = originalText.split("\n");
  const states: RenderedListLineState[] = [];

  let originalIndex = 0;
  let consumed = 0;

  for (const rendered of renderedLines) {
    // desync fallback (shouldn't happen): treat the rendered line standalone
    const originalLine =
      originalIndex < originalLines.length
        ? originalLines[originalIndex]
        : rendered;
    const match = matchBulletLine(originalLine);
    states.push({
      originalLineIndex: Math.min(originalIndex, originalLines.length - 1),
      isListLine: !!match,
      hasMarker: !!match && consumed === 0,
      checked: !!match && isCheckedMarker(match.marker),
      rtl: isLineRTL(originalLine),
    });
    consumed += stripWhitespace(rendered).length;
    if (consumed >= stripWhitespace(originalLine).length) {
      originalIndex++;
      consumed = 0;
    }
  }

  return states;
};

/**
 * Deterministic sentence split for the convert action's single-paragraph
 * mode. Terminators stay attached to their sentence; results are trimmed and
 * empties dropped; input without any terminator yields itself.
 */
export const splitIntoSentences = (paragraph: string): string[] => {
  const sentences = paragraph
    .split(SENTENCE_SPLIT_RE)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
  return sentences.length ? sentences : [];
};

/**
 * Convert plain text to a bullet list:
 * - more than one non-empty line → each non-empty line becomes a "• " bullet
 *   (leading indent preserved, blank lines preserved, already-bulleted lines
 *   left unchanged — the conversion is idempotent);
 * - a single paragraph → split into sentences, one bullet per sentence.
 */
export const convertTextToBullets = (text: string): string => {
  const lines = text.split("\n");
  const nonEmpty = lines.filter((line) => line.trim());

  if (nonEmpty.length > 1) {
    return lines
      .map((line) => {
        if (!line.trim() || matchBulletLine(line)) {
          return line;
        }
        const indent = line.match(/^[ ]*/)![0];
        return `${indent}${BULLET_PRIMARY} ${line.slice(indent.length)}`;
      })
      .join("\n");
  }

  const paragraph = nonEmpty[0];
  if (!paragraph || matchBulletLine(paragraph)) {
    return text;
  }

  return splitIntoSentences(paragraph.trim())
    .map((sentence) => `${BULLET_PRIMARY} ${sentence}`)
    .join("\n");
};

/** true when every line is only whitespace and/or a bare list marker */
export const isBulletPlaceholderText = (text: string): boolean =>
  text.split("\n").every((line) => /^[ ]*[•◦☐☑✓]?[ ]*$/.test(line));
